// A screen's URL-encoded view state (ui-conventions.md #5, #6 — see
// src/core/README.md "URL-state"). Every user-visible view parameter
// (grouping, filters, toggles, sub-views, …) belongs in the URL so it
// survives reload and is shareable; an unknown or out-of-range value on the
// URL must never silently degrade — it is named in a console.warn and
// dropped, not swallowed.
//
//   const { sel, sort, hidden } = useViewState({
//     sel: { type: 'string' },
//     sort: { type: 'string', values: ['name', 'id'] },
//     hidden: { type: 'boolean', default: false },
//   })
//
// Returns one reactive Ref per declared key, two-way-synced with the route
// query via router.replace (never push — a param tweak is not a new history
// entry). A param at its default is omitted from the URL; only a
// non-default value is written.
import { effectScope, ref, watch, type EffectScope, type Ref } from 'vue'
import {
  useRoute,
  useRouter,
  type LocationQuery,
  type LocationQueryValue,
  type Router,
  type RouteLocationNormalizedLoaded,
} from 'vue-router'

export type ParamKind = 'string' | 'number' | 'boolean' | 'string-array'

type ParamValueOf<K extends ParamKind> = K extends 'string'
  ? string
  : K extends 'number'
    ? number
    : K extends 'boolean'
      ? boolean
      : K extends 'string-array'
        ? string[]
        : never

export interface ParamSpec<K extends ParamKind = ParamKind> {
  type: K
  default?: ParamValueOf<K>
  /** The accepted set. For 'string' the whole value is checked; for
   *  'string-array' every element is. Unused for 'number'/'boolean'. */
  values?: readonly string[]
}

export type ViewStateSchema = Record<string, ParamSpec>

export type ViewStateRefs<S extends ViewStateSchema> = {
  [K in keyof S]: Ref<ParamValueOf<S[K]['type']>>
}

interface CacheEntry {
  routeKey: string
  schema: Record<string, ParamSpec>
  refs: Record<string, Ref<unknown>>
  /** Owns every watcher below. Detached, so it outlives any one page
   *  component — see ensureRouteWatcher for why that matters. */
  scope: EffectScope
  routeWatcherReady: boolean
  reconcilePending: boolean
}

// Keyed by route name (falling back to path for unnamed routes), never by
// the full path+query — the set of named routes in the app is small and
// fixed, so one entry per route living for the app's session is the same
// shape of tradeoff as a Pinia store, not an unbounded leak. It is what
// makes "multiple components on one page share a single instance" work
// without provide/inject boilerplate at every page's root.
const registry = new Map<string, CacheEntry>()

function routeKeyOf(name: unknown, path: string): string {
  return typeof name === 'string' ? name : path
}

function currentRouteMatches(entry: CacheEntry, route: RouteLocationNormalizedLoaded): boolean {
  return routeKeyOf(route.name, route.path) === entry.routeKey
}

function firstString(
  value: LocationQueryValue | LocationQueryValue[] | undefined,
): string | undefined {
  if (value === undefined || value === null) return undefined
  if (Array.isArray(value)) {
    const first = value.find((v) => v !== null)
    return first ?? undefined
  }
  return value
}

function zeroValue(kind: ParamKind): unknown {
  switch (kind) {
    case 'string':
      return ''
    case 'number':
      return 0
    case 'boolean':
      return false
    case 'string-array':
      return []
  }
}

function defaultOf(spec: ParamSpec): unknown {
  return spec.default ?? zeroValue(spec.type)
}

function encode(kind: ParamKind, value: unknown): string {
  switch (kind) {
    case 'string':
      return value as string
    case 'number':
      return String(value as number)
    case 'boolean':
      return (value as boolean) ? 'true' : 'false'
    case 'string-array':
      return (value as string[]).join(',')
  }
}

function valuesEqual(kind: ParamKind, a: unknown, b: unknown): boolean {
  if (kind === 'string-array') {
    const arrA = a as string[]
    const arrB = b as string[]
    return arrA.length === arrB.length && arrA.every((v, i) => v === arrB[i])
  }
  return a === b
}

/** Parses and validates one key's raw query value, warning and falling back
 *  to the default on anything that doesn't fit the schema — never silently. */
function parseKey(key: string, spec: ParamSpec, raw: string | undefined): unknown {
  const fallback = defaultOf(spec)
  if (raw === undefined) return fallback

  let value: unknown
  if (spec.type === 'number') {
    const n = Number(raw)
    if (Number.isNaN(n)) {
      console.warn(
        `useViewState: "${key}" has a non-numeric value "${raw}" — falling back to the default.`,
      )
      return fallback
    }
    value = n
  } else if (spec.type === 'boolean') {
    value = raw === 'true'
  } else if (spec.type === 'string-array') {
    value = raw === '' ? [] : raw.split(',')
  } else {
    value = raw
  }

  const accepted = spec.values
  if (accepted) {
    const toCheck = spec.type === 'string-array' ? (value as string[]) : [value as string]
    const bad = toCheck.filter((v) => !accepted.includes(v))
    if (bad.length > 0) {
      console.warn(
        `useViewState: "${key}" has an unrecognised value ${JSON.stringify(bad)} — ` +
          `accepted values are [${accepted.join(', ')}]. Falling back to the default.`,
      )
      return fallback
    }
  }

  return value
}

/** Rebuilds the query from the entry's CURRENT ref values — only non-default
 *  keys are written, and any key outside the schema is dropped by
 *  construction. Calls router.replace only if that differs from the route's
 *  actual query, so an already-canonical URL is never touched. */
// ── global params (ui-conventions.md #97) ──
//
// Shell-level scope (the top-bar competition/season filter) rides the SAME
// URL as every view param (#5: shareable), but belongs to no single route's
// schema. Registered keys are exempt from the unknown-param warning and are
// PRESERVED by canonicalization instead of stripped. The global-scope store
// (src/stores/globalScope.ts) is their one owner — a view never declares
// them in its own schema.
const globalParams = new Set<string>()

/** Registers shell-owned query params. Idempotent; call at store setup. */
export function registerGlobalParams(...keys: string[]): void {
  for (const k of keys) globalParams.add(k)
}

// ── ONE writer for the whole query (fsgadmin/fsgnext#85) ──
//
// The scope and every view param share one query string, and each used to
// write it with a `router.replace` that MERGED the other half out of
// `route.query`. A replace resolves its guards asynchronously, so the second
// writer of a tick reads a route that has not caught up and re-writes the
// first writer's OLD value over its new one — silently. And the shell's
// router guard then reads that stale echo back into the store, so the revert
// survives in the state too, not just in the URL.
//
// That is not a rare interleaving: a view param whose value FOLLOWS the scope
// (the scoring anchor's `ev`/`ctx`, whose defaults key on the scoped seasons)
// is written by a pre-flush watcher in the very same tick the scope store was
// mutated in. Picking a ranking from another season while a scope was set
// therefore snapped straight back — the owner's #85 report.
//
// The cure is to stop merging from the URL at all: each half is written from
// its OWNER (view params from their refs, shell params from the store), so
// neither can resurrect the other's stale value no matter how the replaces
// interleave.
let globalOwner: { router: Router; read: () => Record<string, string> } | null = null

/**
 * Registers the shell-owned params' one source of truth. Called by
 * `installGlobalScope` for the router it wires; scoped to that router so a
 * second app/router instance (tests) can never read another one's store.
 */
export function provideGlobalParams(router: Router, read: () => Record<string, string>): void {
  globalOwner = { router, read }
}

/** The owner's CURRENT values, or null when nothing has claimed them — then
 *  the URL is all there is to pass through, which is the pre-#85 behaviour
 *  and the right one for a router with no shell wired to it. */
function ownedGlobalParams(router: Router): Record<string, string> | null {
  return globalOwner && globalOwner.router === router ? globalOwner.read() : null
}

function applyGlobalParams(
  query: LocationQuery,
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  const owned = ownedGlobalParams(router)
  for (const key of globalParams) {
    const raw = owned ? owned[key] : firstString(route.query[key])
    if (raw !== undefined && raw !== '') query[key] = raw
    else delete query[key]
  }
}

/**
 * Re-writes the CURRENT route's canonical query — every shell param from its
 * owner, every view param from its ref. The global-scope store calls this
 * instead of merging the query itself, so both halves of the URL have exactly
 * one writer (see the note above).
 */
export function writeGlobalParams(router: Router): void {
  const route = router.currentRoute.value
  const entry = registry.get(routeKeyOf(route.name, route.path))
  if (entry) {
    writeCanonicalQuery(entry, route, router)
    return
  }
  // No view state on this route — nothing of this route's is ours to
  // canonicalize, so the scope is written onto whatever else the URL carries.
  const query: LocationQuery = { ...route.query }
  applyGlobalParams(query, route, router)
  void router.replace({ path: route.path, query })
}

function writeCanonicalQuery(
  entry: CacheEntry,
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  const query: LocationQuery = {}
  // Shell-owned params come from their owner, never from the route — they are
  // not this route's to drop, and not this route's to resurrect either.
  applyGlobalParams(query, route, router)
  for (const key of Object.keys(entry.schema)) {
    const spec = entry.schema[key]!
    const value = entry.refs[key]!.value
    if (!valuesEqual(spec.type, value, defaultOf(spec))) {
      query[key] = encode(spec.type, value)
    }
  }

  const currentKeys = Object.keys(route.query)
  const nextKeys = Object.keys(query)
  const same =
    currentKeys.length === nextKeys.length &&
    nextKeys.every((k) => firstString(route.query[k]) === query[k])
  if (same) return

  void router.replace({ path: route.path, query })
}

function syncRefsFromQuery(entry: CacheEntry, query: LocationQuery): void {
  for (const [key, spec] of Object.entries(entry.schema)) {
    const parsed = parseKey(key, spec, firstString(query[key]))
    const target = entry.refs[key]!
    if (!valuesEqual(spec.type, target.value, parsed)) target.value = parsed
  }
}

function warnUnknownParams(entry: CacheEntry, query: LocationQuery): void {
  const unknown = Object.keys(query).filter((k) => !(k in entry.schema) && !globalParams.has(k))
  if (unknown.length === 0) return
  const accepted = Object.keys(entry.schema)
  for (const key of unknown) {
    console.warn(
      `useViewState: unrecognised query param "${key}" on this route — ` +
        `accepted params are [${accepted.join(', ')}]. Dropped.`,
    )
  }
}

function ensureRouteWatcher(
  entry: CacheEntry,
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  if (entry.routeWatcherReady) return
  entry.routeWatcherReady = true

  // The cache entry outlives any one page component (see the registry
  // comment above), so the watchers must too. A plain watch() here would be
  // disposed with the setup()-calling component's effect scope on unmount,
  // leaving a revisited route with dead sync: stale refs that
  // scheduleReconcile then writes back OVER the incoming link's query
  // (ui-report 01a040c4). The detached scope ties them to the entry's
  // session lifetime instead — same tradeoff as the registry itself.
  //
  // Route → refs. Guarded to this entry's own route: `useRoute()` is one
  // singleton for whatever route is CURRENTLY active app-wide, so without
  // the guard a watcher created while on route A would react to (and
  // clobber) route B's query after navigating away.
  entry.scope.run(() => {
    watch(
      () => (currentRouteMatches(entry, route) ? route.query : null),
      (query) => {
        if (query === null) return
        syncRefsFromQuery(entry, query)
        warnUnknownParams(entry, query)
        writeCanonicalQuery(entry, route, router)
      },
      { deep: true },
    )
  })
}

/**
 * Registers one key, and — the other half — the ref→route watcher that
 * writes its value back to the URL. A component setting `sort.value = 'id'`
 * writes straight back to the query; router.replace, so this never spams
 * browser history. The guard inside keeps a stray ref write from touching
 * another route's URL.
 *
 * ONE WATCHER PER KEY, deliberately, rather than a single watcher over the
 * whole schema: keys are registered incrementally — one page composes
 * several composables that each declare their own params (useTableSort's
 * `sort`/`desc` alongside the page's own `sel`/`q`/facets), and a composable
 * called after the first registration would have its refs created AFTER a
 * schema-wide watcher had already collected its dependencies. Vue tracks
 * what the source function READ on its first run, so those later refs would
 * never be tracked and their changes would silently never reach the URL —
 * the sort would work on screen and be missing from the shared link (#5).
 */
function registerKey(
  entry: CacheEntry,
  key: string,
  spec: ParamSpec,
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  if (entry.schema[key]) return // already registered by another component sharing this entry
  entry.schema[key] = spec
  const target = ref(parseKey(key, spec, firstString(route.query[key])))
  entry.refs[key] = target

  entry.scope.run(() => {
    watch(
      target,
      () => {
        if (!currentRouteMatches(entry, route)) return
        writeCanonicalQuery(entry, route, router)
      },
      { deep: true },
    )
  })
}

/** Deferred one microtask so every component's synchronous setup() in the
 *  same render pass gets to register its keys first — otherwise the first
 *  caller would see the second caller's key as "unknown" for one tick. */
function scheduleReconcile(
  entry: CacheEntry,
  route: RouteLocationNormalizedLoaded,
  router: Router,
): void {
  if (entry.reconcilePending) return
  entry.reconcilePending = true
  void Promise.resolve().then(() => {
    entry.reconcilePending = false
    if (!currentRouteMatches(entry, route)) return
    warnUnknownParams(entry, route.query)
    writeCanonicalQuery(entry, route, router)
  })
}

/**
 * Test seam: forget every route's cached view state. The registry is bound to
 * the router it first saw, which is right for the app (one router for its
 * lifetime) but not for a test file that mounts a fresh router per test.
 */
export function resetViewStateRegistry(): void {
  for (const e of registry.values()) e.scope.stop()
  registry.clear()
}

export function useViewState<S extends ViewStateSchema>(schema: S): ViewStateRefs<S> {
  const route = useRoute()
  const router = useRouter()
  const routeKey = routeKeyOf(route.name, route.path)

  let entry = registry.get(routeKey)
  if (!entry) {
    entry = {
      routeKey,
      schema: {},
      refs: {},
      scope: effectScope(true),
      routeWatcherReady: false,
      reconcilePending: false,
    }
    registry.set(routeKey, entry)
  }

  for (const [key, spec] of Object.entries(schema)) {
    registerKey(entry, key, spec, route, router)
  }

  ensureRouteWatcher(entry, route, router)
  scheduleReconcile(entry, route, router)

  const result = {} as Record<string, Ref<unknown>>
  for (const key of Object.keys(schema)) {
    result[key] = entry.refs[key]!
  }
  return result as ViewStateRefs<S>
}
